import React, { useRef } from "react";
import Icon from "@mdi/react";
import { mdiMenuLeft, mdiMenuRight } from "@mdi/js";
import {
  getGameLeague,
  getGameOddsText,
  getGameRound,
  useGameAccess,
  getGamePick,
  useGameProgressAndChannel,
} from "src/services/EventService";
import TeamLogo from "src/components/Team/TeamLogo";
import styles from "./styles.scss";
import { useContainerQuery } from "src/services/UtilService";
import { COMP_GREEN } from "src/colors";
import { UpgradeButton } from "../UpgradeButton";
import { renderRatingByValue } from "src/services/ValueDataService";
import { useFreeTrialClaimed } from "src/services/UserService";
import { BetQLService } from "src/common-services";

const { getTeamConfRecord, getTeamPitcher, getTeamPitcherRecord, getTeamRank, getTeamRecord } = BetQLService;

const GreenArrow = ({ team }: { team: "home" | "away" }) => {
  if (!team) {
    return null;
  }

  return (
    <div className="green-arrow" style={team === "away" ? { right: "-25px" } : { left: "-25px" }}>
      <Icon path={team === "away" ? mdiMenuLeft : mdiMenuRight} size="30px" color={COMP_GREEN} />
    </div>
  );
};

const TeamHeader = ({
  game,
  team: _team,
  align,
  size,
  showRating = false,
}: {
  game: any;
  team: "home" | "away";
  align: "left" | "right";
  size?: "small" | "medium" | "large";
  showRating?: boolean;
}) => {
  const team = game[`${_team}Team`];
  const league = getGameLeague(game);

  // Green arrow
  const access = useGameAccess(game);
  const pick = access && getGamePick({ game });
  const showArrow = pick?.outcome === _team;

  const logo = (
    <TeamLogo
      size={50}
      leagueName={league.imagePath}
      teamAbbreviation={team[league.sport.name === "Tennis" ? "country" : "preferredAbbreviation"]} // prettier-ignore
    />
  );

  const fullName = size === "small" ? team.lastName : team.fullName;

  const rank = game[`${_team}TeamSeed`] ?? getTeamRank(team, league);

  const record = getTeamRecord(team, league);

  const confRecord = getTeamConfRecord(team, league);

  const pitcher = getTeamPitcher(game[`${_team}TeamPitcher`], league);

  const pitcherRecord = getTeamPitcherRecord(game[`${_team}TeamPitcher`], league); // prettier-ignore

  return (
    <div className={`team-header ${align}`}>
      <div className="team-logo">{logo}</div>
      <div className="team-details">
        <div className="team-full-name">
          {showRating && showArrow && <GreenArrow team={_team} />}
          {rank && <span className="team-rank">{rank}</span>}
          {fullName}
        </div>
        <div className="team-records">
          {record && <span className="team-record">{record}</span>}
          {confRecord && <span className="team-record">, {confRecord}</span>}
        </div>
        {pitcher && <div className="team-pitcher">{pitcher}</div>}
        {pitcherRecord && <div className="team-pitcher-record">{pitcherRecord}</div>}
      </div>
    </div>
  );
};

const GameMetadata = ({ game, showRating = false }: { game: any; showRating?: boolean }) => {
  const trialClaimed = useFreeTrialClaimed();
  const location = game.stadium || game.location;
  const round = getGameRound(game);

  let locationRound = location || round || "";
  if (location && round) {
    locationRound = `${location}, ${round}`;
  }

  const gameTimeArr = useGameProgressAndChannel(game).match(/.{1,22}(\s|$)/g);

  const access = useGameAccess(game);
  const pick = access && getGamePick({ game });

  return (
    <div className="game-metadata">
      <div className="game-datetime">
        <span>{gameTimeArr?.[0]}</span>
        {Boolean(gameTimeArr?.[1]) && <span>{gameTimeArr?.[1]}</span>}
      </div>
      {locationRound && <div className="game-location-round">{locationRound}</div>}
      <div className="game-odds">{getGameOddsText(game)}</div>
      {showRating && (
        <div className="game-rating">
          {pick ? (
            renderRatingByValue(pick.stars)
          ) : (
            <UpgradeButton className="upgrade-button" game={game} source="Game Header">
              {trialClaimed ? "Upgrade" : "Start Free Trial"}
            </UpgradeButton>
          )}
        </div>
      )}
    </div>
  );
};

const GameHeader = ({ game, showRating = false }: { game: any; showRating?: boolean }) => {
  const container = useRef<any>(null);
  const size = useContainerQuery(container, {
    small: 400,
    medium: 700,
    large: Infinity,
  });

  return (
    <div ref={container} className="game-header">
      <div className="game-header-inner">
        <TeamHeader
          game={game}
          team="away"
          align={size === "small" ? "left" : "right"}
          size={size}
          showRating={showRating}
        />
        <GameMetadata game={game} showRating={showRating} />
        <TeamHeader game={game} team="home" align={size === "small" ? "right" : "left"} size={size} />
      </div>
      <style jsx>{styles}</style>
    </div>
  );
};

export default GameHeader;
